import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { apiClient } from '@/lib/apiClient';

interface RevenueMeetingSummary {
  id: string;
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
  closedWonCount: number;
  closedWonValue: number;
  pipelineAdvancedCount: number;
  atRiskCount: number;
  forecastValue: number | null;
  targetValue: number | null;
}

interface RevenueMeetingDetail extends RevenueMeetingSummary {
  digestJson: string;
}

function formatDate(s: string) {
  return new Date(s).toLocaleDateString();
}

function formatCurrency(v: number | null) {
  if (v == null) return '—';
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 });
}

export function RevenueMeetingPage() {
  const { t } = useTranslation();
  const [meetings, setMeetings] = useState<RevenueMeetingSummary[]>([]);
  const [selected, setSelected] = useState<RevenueMeetingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<RevenueMeetingSummary[]>('/revenue-meetings');
      setMeetings(data);
      if (data.length > 0) {
        selectMeeting(data[0].id);
      }
    } catch {
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  async function selectMeeting(id: string) {
    setLoadingDetail(true);
    try {
      const { data } = await apiClient.get<RevenueMeetingDetail>(`/revenue-meetings/${id}`);
      setSelected(data);
    } catch {
      setSelected(null);
    } finally {
      setLoadingDetail(false);
    }
  }

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Breadcrumbs crumbs={[{ label: t('revenueCadence.meeting_title') }]} />
      <h1 className="text-2xl font-semibold mt-2">{t('revenueCadence.meeting_title')}</h1>

      {loading ? (
        <p className="text-muted-foreground mt-6 text-sm">{t('common.loading')}</p>
      ) : meetings.length === 0 ? (
        <p className="text-muted-foreground mt-6 text-sm">{t('revenueCadence.meeting_empty')}</p>
      ) : (
        <div className="mt-6 flex gap-6">
          {/* Left: meeting list */}
          <div className="w-64 shrink-0 space-y-1">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              {t('revenueCadence.meeting_list_title')}
            </h2>
            {meetings.map((m) => (
              <button
                key={m.id}
                onClick={() => selectMeeting(m.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selected?.id === m.id
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'hover:bg-muted text-muted-foreground'
                }`}
              >
                <div>{formatDate(m.periodStart)} — {formatDate(m.periodEnd)}</div>
                <div className="text-xs mt-0.5 opacity-70">{formatDate(m.generatedAt)}</div>
              </button>
            ))}
          </div>

          {/* Right: detail */}
          <div className="flex-1 min-w-0">
            {loadingDetail ? (
              <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
            ) : selected ? (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold">
                    {t('revenueCadence.meeting_subtitle', {
                      start: formatDate(selected.periodStart),
                      end: formatDate(selected.periodEnd),
                    })}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('common.loading').replace(t('common.loading'), '')}
                    Generated {formatDate(selected.generatedAt)}
                  </p>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('revenueCadence.stat_closed_won')}</p>
                    <p className="text-2xl font-semibold mt-1">{selected.closedWonCount}</p>
                    <p className="text-sm text-muted-foreground">{formatCurrency(selected.closedWonValue)}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('revenueCadence.stat_pipeline')}</p>
                    <p className="text-2xl font-semibold mt-1">{selected.pipelineAdvancedCount}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('revenueCadence.stat_at_risk')}</p>
                    <p className={`text-2xl font-semibold mt-1 ${selected.atRiskCount > 0 ? 'text-destructive' : ''}`}>
                      {selected.atRiskCount}
                    </p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('revenueCadence.stat_forecast')}</p>
                    <p className="text-2xl font-semibold mt-1">{formatCurrency(selected.forecastValue)}</p>
                  </div>
                  {selected.targetValue != null && (
                    <div className="border rounded-lg p-4">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('revenueCadence.stat_target')}</p>
                      <p className="text-2xl font-semibold mt-1">{formatCurrency(selected.targetValue)}</p>
                    </div>
                  )}
                </div>

                {/* Raw digest */}
                {selected.digestJson && (() => {
                  try {
                    const digest = JSON.parse(selected.digestJson);
                    return (
                      <div className="border rounded-lg p-4 text-sm space-y-3">
                        {digest.highlights && digest.highlights.length > 0 && (
                          <div>
                            <p className="font-medium mb-1">Highlights</p>
                            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                              {digest.highlights.map((h: string, i: number) => (
                                <li key={i}>{h}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {digest.risks && digest.risks.length > 0 && (
                          <div>
                            <p className="font-medium mb-1 text-destructive">Risks</p>
                            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                              {digest.risks.map((r: string, i: number) => (
                                <li key={i}>{r}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  } catch {
                    return null;
                  }
                })()}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
